import { useEffect, useRef, useState } from "react";
import Configurator from "../config/Configurator.js";
import { useDispatch, useSelector } from "react-redux";
import { setLedLive } from "../services/slices/appConfig.js";
import { clearCache } from "../api/Fetchers/fetch.old.data.js";
import { clearData } from "../Components/AddElementPanel2/utils/clear.data.js";
import axios from "axios";
import { clearUserData } from "../services/slices/usersSlice.js";
import { buildWsMessageData } from "../utils/Wensockets/tags.utils.js";

let wsInstance = null;

const useWebSocket = (
  isLoggedIn,
  liveFlag,
  processLiveData,
  setIsLoggedIn,
  isSliding,
  isModalOpen
) => {
  /*** * RECONNECTION ATTEMPTS * ***/

  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const dispatch = useDispatch();
  const terminate = useRef(false);
  const lastKnownRecords = useRef({});
  const reconnectTimeout = useRef(null);

  /*** * SENSORS THAT APPLICATION NEEDS * ***/

  const wsRequestData = useSelector((state) => state.appConfig.wsRequestData);

  /*** * AUTORIZAATION VARIABLES* ***/

  const apiKeyRef = useRef(null);
  const userIDRef = useRef(null);

  const sendMessage = (message) => {
    if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
      const authMessage = {
        ...message,
        auth: {
          idUser: parseInt(userIDRef.current),
          session: apiKeyRef.current,
        },
      };
      wsInstance.send(JSON.stringify(authMessage));
    }
  };

  const logoutUser = async () => {
    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: (await Configurator()).API_URL + "/v2/io/users/logout",
      headers: {
        ...axios.defaults.headers.common,
        Session: localStorage.getItem("apiKey"),
        "User-Id": localStorage.getItem("userID"),
      },
    };

    dispatch(clearUserData());
    localStorage.clear();
    sessionStorage.clear();

    await axios
      .request(config)
      .then((response) => {
        if (response.status === 200) {
          localStorage.clear();
          sessionStorage.clear();
        }
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      let wsUrl;

      const config = await Configurator();
      const apiKey = localStorage.getItem("apiKey");
      const userID = localStorage.getItem("userID");

      apiKeyRef.current = apiKey;
      userIDRef.current = userID;

      const host = new URL(config.API_URL).host;
      const wsPrefix = config.API_WS_PREFIX;
      wsUrl = `${wsPrefix}://${host}/api/v2/io/records/abstract/live`;

      if (!apiKey || !userID) {
        console.log("Missing or invalid API key or user ID");
        return;
      }

      wsInstance = new WebSocket(wsUrl);

      wsInstance.onopen = () => {
        setReconnectAttempts((prevReconnect) => prevReconnect + 1);
        const firstMsg = JSON.stringify({
          auth: { isUser: parseInt(userID), session: apiKey },
        });
      };

      const missingRecordCount = {};
      wsInstance.onmessage = (event) => {
        try {
          if (!isLoggedIn) {
            wsInstance.close(1000, "User logged out");
            return;
          }

          const data = JSON.parse(event.data);
          if (data?.status?.errCode === 3.0) {
            setIsLoggedIn(false);
            localStorage.setItem("userID", "");
            return;
          }
          if (data?.status?.errCode > 0) {
            return;
          }

          if (liveFlag.current && !isSliding.current) {
            //TAKE SHIP SERIALS OBJECT WITH THE LATEST RECORDS
            const records = data?.data?.records || {};

            const liveData = {};

            Object.keys(records).forEach((key) => {
              const rec = records[key].data;
              liveData[key] = {
                timestamp: records[key].metadata?.LatestTimestamp,
                interval: wsRequestData?.msIntInterval || 250,
              };
              // 1) If repeat === true, restore lastKnownRecords and update LED:
              if (rec.repeat) {
                const last = lastKnownRecords.current[key] || {};
                records[key].data.latestRecord = last;
                return;
              }

              // 2) Otherwise, if latestRecord is empty → normal “10 empties” logic:
              if (Object.keys(rec.latestRecord || {}).length === 0) {
                missingRecordCount[key] = (missingRecordCount[key] || 0) + 1;

                if (missingRecordCount[key] >= 10) {
                  lastKnownRecords.current[key] = rec.latestRecord;
                  missingRecordCount[key] = 0;
                } else {
                  // fallback to previous record
                  records[key].data.latestRecord =
                    lastKnownRecords.current[key] || {};
                }
              } else {
                // 3) latestRecord is non-empty → store it and turn LED on
                lastKnownRecords.current[key] = rec.latestRecord;
                missingRecordCount[key] = 0;
              }
            });

            dispatch(setLedLive(liveData));

            if (Object.keys(data?.data?.records).length > 0) {
              /*** * SET THE DATA * ***/

              processLiveData(records);
            } else {
            }
          }
        } catch (e) {
          console.log(e);
        }
      };

      wsInstance.onclose = () => {
        if (isLoggedIn) {
          // Attempt to reconnect after 5 seconds
          reconnectTimeout.current = setTimeout(() => {
            initializeWebSocket();
          }, 5000);
        } else {
          wsInstance.current = null;
        }
      };

      wsInstance.onerror = (error) => {
        console.log("WebSocket error", error);
        if (isLoggedIn && !terminate.current) {
        } else {
          wsInstance = null;
        }
      };
    };

    if (isLoggedIn) {
      initializeWebSocket();
    } else if (wsInstance) {
      logoutUser();
      clearCache();

      terminate.current = true;
      processLiveData({});
      clearData(dispatch);

      wsInstance.close(1000, "User logged out");
    }

    return () => {
      terminate.current = true;
      if (wsInstance) {
        wsInstance.close(1000, "User logged out");
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, [isLoggedIn]);

  /*** * LISTEN ON CHANGES OF DESIRED SENSORS * ***/

  useEffect(() => {
    if (wsRequestData === "error") {
      setIsLoggedIn(false);
      return;
    }

    const wsOpen = wsInstance && wsInstance.readyState === WebSocket.OPEN;
    if (!wsOpen || isModalOpen) return;

    const data = buildWsMessageData(wsRequestData);
    if (!data) return;

    sendMessage({ data });
  }, [wsRequestData, wsInstance, reconnectAttempts, isModalOpen]);

  return { wsInstance };
};

export default useWebSocket;
